import { faDatabase } from '@fortawesome/free-solid-svg-icons'
import { Box } from '@mui/material'
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ROUTES } from '@/constants'
import { StructureObjectDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppStore,
	useAuthContext,
} from '@/hooks'
import mainColors from '@/styles/mainColors'

import { AddModal } from '../../../AddModal'
import { useSelectedSystem, useTreePanelOperations } from '../../TreePanelContext'
import { SelectionCard, SelectionOption } from './SelectionCard'

type SystemSelectionProps = {
	/** Compact control for the dark top navbar. */
	variant?: 'default' | 'navbar'
}

export const SystemSelection = ({
	variant = 'default',
}: SystemSelectionProps) => {
	const { t } = useAppContext()
	const auth = useAuthContext()
	const navigate = useNavigate()
	const location = useLocation()
	const reduxRoots = useAppStore((state) => state.node.roots)
	const reduxNodes = useAppStore((state) => state.node.nodes)
	const { selectedSystemId, setSelectedSystemId, requestSystemContextMenu } =
		useSelectedSystem()
	const { refreshSystemsSilent } = useTreePanelOperations()
	const [createModalOpen, setCreateModalOpen] = useState(false)
	const isNavbar = variant === 'navbar'
	const canCreateSystem = auth.canCreateSystem()

	const systemOptions = useMemo<SelectionOption[]>(
		() =>
			reduxRoots
				.map((id) => {
					const node = reduxNodes[id]
					if (!node) return null
					return {
						id: String(node.id),
						label: node.name || node.code || String(node.id),
					}
				})
				.filter(Boolean) as SelectionOption[],
		[reduxNodes, reduxRoots],
	)

	// Keep stored selection while roots are still loading — clearing it caused
	// refresh to lose "last opened system" and briefly flash all systems in the tree.
	useEffect(() => {
		if (systemOptions.length === 0) return
		if (!systemOptions.some((option) => option.id === selectedSystemId)) {
			setSelectedSystemId(systemOptions[0].id)
		}
	}, [selectedSystemId, setSelectedSystemId, systemOptions])

	const selectedNode =
		selectedSystemId != null ? reduxNodes[Number(selectedSystemId)] : undefined

	const openSystemMenuAt = useCallback(
		(pageX: number, pageY: number) => {
			if (!selectedNode) return
			if (location.pathname !== ROUTES.PROJECT_VIEW) {
				navigate(ROUTES.PROJECT_VIEW)
			}
			requestSystemContextMenu(pageX, pageY)
		},
		[location.pathname, navigate, requestSystemContextMenu, selectedNode],
	)

	const handleSystemMenu = useCallback(
		(event: MouseEvent) => {
			event.preventDefault()
			event.stopPropagation()
			openSystemMenuAt(event.pageX, event.pageY)
		},
		[openSystemMenuAt],
	)

	const handleCreateSystem = useCallback(() => {
		if (location.pathname !== ROUTES.PROJECT_VIEW) {
			navigate(ROUTES.PROJECT_VIEW)
		}
		setCreateModalOpen(true)
	}, [location.pathname, navigate])

	const handleCreateRefresh = useCallback(() => {
		refreshSystemsSilent().catch(() => undefined)
	}, [refreshSystemsSilent])

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					width: isNavbar ? 240 : '100%',
					minWidth: isNavbar ? 240 : 0,
					maxWidth: isNavbar ? 240 : undefined,
					flexShrink: isNavbar ? 0 : undefined,
					height: 32,
				}}
				data-testid="system-selection"
			>
				<SelectionCard
					compact
					variant={isNavbar ? 'navbar' : 'default'}
					icon={faDatabase}
					iconBgColor="transparent"
					iconColor={isNavbar ? 'rgba(255,255,255,0.85)' : mainColors.danger}
					title={
						selectedNode?.name ||
						selectedNode?.code ||
						t('SYSTEM_SELECTION_NO_SYSTEM')
					}
					options={systemOptions}
					selectedId={selectedSystemId ?? ''}
					onSelect={setSelectedSystemId}
					emptyLabel={t('SYSTEM_SELECTION_NO_SYSTEM')}
					ariaLabel={t('SYSTEM_SELECTION_SELECT_SYSTEM')}
					onSystemMenu={handleSystemMenu}
					systemMenuAriaLabel={t('SYSTEM_SELECTION_SYSTEM_MENU')}
					onContextMenu={handleSystemMenu}
					onCreateSystem={canCreateSystem ? handleCreateSystem : undefined}
					createSystemLabel={t('CREATE_SYSTEM')}
				/>
			</Box>
			{createModalOpen && (
				<AddModal
					onClose={() => setCreateModalOpen(false)}
					onRefresh={handleCreateRefresh}
					type={StructureObjectDto.TypeEnum.SYSTEM}
				/>
			)}
		</>
	)
}
